import { DeleteFilled } from '@ant-design/icons';
import { DatePicker, Divider, Dropdown, Input, InputNumber } from 'antd';
import styled from 'styled-components';
import { CustomTable } from '../../styles/Global';
import { $background, $black, $primary } from '../../styles/Variables';
import Button from '../Button/Button';
import CustomSelect from '../Form/FormItem/Select/Select';

const { Search } = Input;

export const ActionsStyled = styled.div`
  border-radius: 8px;
  background-color: ${$background};
  padding: 20px;
  width: 164px;
  position: sticky;
  top: 90px;
`;

export const ActionsSyncStyled = styled(ActionsStyled)`
  top: 200px;

  .ant-btn-loading {
    &::before {
      display: none;
    }
  }
`;

export const CreateOrderItemWrapper = styled.div<{ $hasVariant?: boolean }>`
  display: grid;
  // Columns: Size, [Variant], Quantity, Cost, Delete (50px fixed).
  // Variant-bearing products (badge_reel, glass_suncatcher) insert an extra
  // flexible column between Size and Quantity.
  grid-template-columns: ${({ $hasVariant }) =>
    $hasVariant ? '1fr 1fr 1fr 1fr 50px' : '1fr 1fr 1fr 50px'};
  column-gap: 10px;
  align-items: end;

  .ant-form-item:not(:last-child) {
    margin-bottom: 0;
  }
`;

export const PickDesignWrapper = styled.div``;

export const StyledSelect = styled(CustomSelect)`
  .ant-select-selector {
    border-radius: 8px !important;
    padding: 1px 3px;
    min-height: 36px;
    border: 1px solid var(--color-border-light) !important;

    .ant-select-selection-item {
      border: none;
      border-radius: 8px;
      padding: 10px 0;
      color: ${$black};
      background-color: transparent;
      font-size: 14px;
      line-height: 14px;
      height: 36px;

      .anticon {
        color: ${$primary};
        font-size: 9px;
      }
    }
  }

  .ant-select-single .ant-select-selector .ant-select-selection-placeholder {
    margin: auto;
  }

  .ant-select-selector .ant-select-selection-search-input {
    height: 30px !important;
  }

  .ant-select-selection-placeholder {
    margin: auto;
  }

  &.ant-select-multiple .ant-select-selection-overflow {
    max-width: 100px !important;
    flex-wrap: nowrap !important;
    overflow-x: auto;
    overflow-y: hidden;
  }

  &.ant-select-multiple
    .ant-select-selection-overflow
    .ant-select-selection-overflow-item
    .ant-select-selection-item {
    height: auto !important;
    padding: 0 !important;
  }
`;

export const OrderItemWrapper = styled.div`
  display: grid;
  grid-template-columns: 100px 1fr auto;
  gap: 20px;
  align-items: end;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--color-border-light);
`;

export const ImageContainer = styled.div<{ $active?: boolean }>`
  border-radius: 16px;
  border: 1px solid var(--color-border-light);
  height: 100px;
  width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $active }) =>
    $active &&
    `
    cursor: pointer !important;
    transition: all 0.3s ease;

    &:hover {
      border-color: #155eef !important;
      color: #155eef;
    }
  `}

  img {
    max-width: 98px;
    max-height: 98px;
    border-radius: 16px;
  }
`;

export const StyledInputNumber = styled(InputNumber)`
  border: 1px solid var(--color-border-light);
  border-radius: 8px;
  height: 36px;

  input {
    height: 34px;
    padding: 6px 12px;
  }
`;

export const ActionText = styled.span<{ $disabled: boolean }>`
  font-weight: 400;
  text-transform: capitalize;
  cursor: pointer;

  ${({ $disabled }) =>
    $disabled &&
    `
    user-events: none;
    cursor: not-allowed;
  `}

  &.process {
    color: #0086ea;
  }

  &.finish {
    color: #e26664;
  }
`;

export const CheckoutDraftRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  p {
    font-weight: 600;
    font-size: 14px;
    color: var(--color-black-bolder);
  }
`;

export const OrderContentWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 24px;
  align-items: start;
  width: 100%;
  max-width: 100%;
  overflow: visible; /* Allow sticky positioning to work */

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

export const OrderFormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 100%;
  overflow: visible; /* Allow content to be visible for sticky positioning */
  min-width: 0; /* Allow flexbox to shrink below content size */
  padding-bottom: 20px;

  .ant-form {
    width: 100%;
    max-width: 100%;

    .ant-form-item {
      .ant-form-item-label {
        text-align: left;

        > label {
          text-align: left;
          justify-content: flex-start;
          padding-bottom: 0;
        }
      }
    }
  }
`;

export const CheckoutDraftContainer = styled.div`
  width: 100%;
  background-color: #f9fafb;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 20px;
  position: sticky;
  bottom: 20px;
  height: fit-content;
  align-self: end;
  z-index: 10;

  /* Ensure sticky works in scrolling containers */
  contain: layout style paint;

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-black-bolder);
    margin: 0 0 16px 0;
  }

  @media (max-width: 1024px) {
    position: static;
    z-index: auto;
  }
`;

export const CheckoutDraftContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const SearchFilterContainer = styled.div<{ $isAdmin?: boolean }>`
  display: grid;
  gap: 12px;
  padding: 20px;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid var(--color-border);
  margin-bottom: 30px;

  ${({ $isAdmin }) =>
    $isAdmin
      ? `grid-template-columns: 1.5fr 2fr 18px 1fr 1fr 1fr 1fr;`
      : `grid-template-columns: 1.5fr 2fr 18px 1fr 1fr 1fr;`}
`;

export const StyledSearch = styled(Search)`
  /* border: 1px solid #6c7ae0; */

  &.ant-input-group-wrapper {
    height: 38px;
  }

  .ant-btn.ant-btn-icon-only {
    width: 36px;
    height: 36px;
  }

  .ant-btn-default {
    border-color: var(--color-border-light);
  }

  input.ant-input {
    height: 36px;
    border: 1px solid var(--color-border-light);
  }
`;

export const StyledDatePicker = styled(DatePicker.RangePicker)`
  &.ant-picker {
    height: 36px;
    border: 1px solid var(--color-border-light);
  }
`;

export const StyledDivider = styled(Divider)`
  height: 24px;
  border-color: var(--color-border-light);
  margin-top: 6px;
`;

export const NestedTable = styled(CustomTable)`
  &.ant-table-wrapper .ant-table-thead > tr > th {
    padding: 4px 10px;
  }

  .ant-table-tbody {
    background-color: #fff;

    > tr > td {
      background-color: #fff !important;
    }
  }
`;

export const NestedTableImage = styled.img`
  max-height: 50px;
  max-width: 100px;
  object-fit: contain;
`;

export const StatusTag = styled.div<{
  $pending?: boolean;
  $process?: boolean;
  $shipped?: boolean;
  $cancel?: boolean;
  $reshipped?: boolean;
  $rejected?: boolean;
  $pendingResend?: boolean;
  $approvedResend?: boolean;
  $fontSize?: number;
}>`
  text-transform: capitalize;
  padding: 2px 8px;
  text-align: center;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: fit-content;
  height: 22px;

  ${({ $fontSize }) =>
    $fontSize &&
    `
      font-size: ${$fontSize}px;
  `}

  ${({ $pending }) =>
    $pending &&
    `
    color: #B54708;
    background: #FFFAEB;
  `}

  ${({ $process }) =>
    $process &&
    `
    color: #175CD3;
    background: #EFF8FF;
  `}

  ${({ $shipped }) =>
    $shipped &&
    `
    color: #027A48;
    background: #ECFDF3;
  `}

  ${({ $reshipped }) =>
    $reshipped &&
    `
    color: #6941C6;
    background: #F9F5FF;
  `}

  ${({ $cancel }) =>
    $cancel &&
    `
    color: var(--color-black-bolder);
    background: #F2F4F7;
  `}

  ${({ $rejected }) =>
    $rejected &&
    `
    color: #ffffff;
    background: #dc143c;
  `}

  ${({ $pendingResend }) =>
    $pendingResend &&
    `
    color: #B93815;
    background: #FEF6EE;
  `}
  
  ${({ $approvedResend }) =>
    $approvedResend &&
    `
    color: #C11574;
    background: #FDF2FA;
  `}
`;

export const CustomSize = styled.span<{
  $2in?: boolean;
  $3in?: boolean;
  $4in?: boolean;
  $5in?: boolean;
  $6in?: boolean;
  $card?: boolean;
}>`
  border-radius: 20px;
  padding: 3px 12px;
  min-width: 40px;
  text-align: center;
  max-width: 60px;
  font-size: 15px;
  font-weight: 600;

  ${({ $2in }) => $2in && `background-color: #e7eaec`}
  ${({ $3in }) => $3in && `background-color: #e6e6e7`}
  ${({ $4in }) => $4in && `background-color: #bee1f7`}
  ${({ $5in }) => $5in && `background-color: #d5edbc`}
  ${({ $6in }) => $6in && `background-color: #e5d0f2`}
  ${({ $card }) => $card && `background-color: #fee5a0`}
`;

export const StyledCountry = styled.p<{ $cad: boolean }>`
  font-weight: 500;

  ${({ $cad }) => $cad && `color: #e59a0f;`}
`;

export const StyledQuantity = styled.span<{ $moreThan1: boolean }>`
  font-weight: 600;
  font-size: 15px;

  ${({ $moreThan1 }) =>
    $moreThan1 &&
    `
    color: #ececd5;
    background-color: #549016;
    border-radius: 20px;
    padding: 3px 12px;
  `}
`;

export const DesignWrapper = styled.div<{ $totalItems: number }>`
  display: grid;
  /* grid-template-columns: repeat(4, 40px) 20px; */
  align-items: end;
  gap: 4px;

  ${({ $totalItems }) => {
    if ($totalItems > 0 && $totalItems <= 3) {
      return `
        grid-template-columns: repeat(${$totalItems}, 60px);

        img {
          max-width: 60px;
          max-height: 60px;
        }
      `;
    }

    if ($totalItems >= 4) {
      return `
        grid-template-columns: repeat(4, 40px) 20px;

        img {
          max-width: 40px;
          max-height: 40px;
        }
      `;
    }
  }}

  img {
    object-fit: contain;
  }
`;

export const Mask = styled.div``;

export const ThreeDot = styled.p`
  font-weight: 600;
`;

export const BulkActionButton = styled(Dropdown)<{ $disabled: boolean }>`
  font-size: 14px;
  height: 35px;
  padding: 0px 14px;
  background: var(--color-white);
  border: ${({ $disabled }) =>
    $disabled ? '1px solid white' : '1px solid var(--color-border-light)'};
  color: ${({ $disabled }) =>
    $disabled ? 'var(--color-primary-disabled)' : 'var(--color-black-bolder)'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
  font-weight: 500;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: border-color 0.3s ease, color 0.3s ease, opacity 0.3s ease;
`;

export const SpinContainer = styled.div`
  position: fixed;
  height: 100%;
  width: 100%;
  display: grid;
  align-items: center;
  z-index: 10;
  top: 0;
  background-color: #cfd5db;
  opacity: 0.5;
  left: 0;
`;

export const SellerDateWrapper = styled.div`
  display: grid;
  gap: 8px;
  align-items: center;
`;

export const StyledStatusFilterItemListContainer = styled.div`
  display: flex;
  width: fit-content;
  gap: 16px;
  margin-top: 16px;
`;

export const StyledStatusFilterItemWrapper = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  width: fit-content;
  gap: 10px;
  padding-bottom: 10px;
  padding-left: 4px;
  padding-right: 4px;
  cursor: pointer;

  ${({ $active }) =>
    $active &&
    `
    border-bottom: 2px solid #155EEF;
  `}
`;

export const StyledStatusFilterItem = styled.p<{ $active: boolean }>`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-light-grey);

  ${({ $active }) =>
    $active &&
    `
    color: #155EEF;
  `}
`;

export const StyledStatusFilterItemCountWrapper = styled.div<{
  $active: boolean;
}>`
  padding: 2px 8px;
  border-radius: 12px;
  background-color: #f2f4f7;

  ${({ $active }) =>
    $active &&
    `
    background-color: #E6F4FF;
  `}
`;

export const OrderTable = styled(CustomTable)`
  margin-top: 20px;

  .ant-table-thead > tr > th {
    padding: 12px 16px;
  }

  .ant-table-row > td > .ant-table-row-expand-icon {
  }
`;

export const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StyledOrderId = styled.p`
  font-weight: 500;
  font-size: 14px;
`;

export const StyledOrderDate = styled.p`
  font-weight: 400;
  font-size: 12px;
  color: var(--color-light-grey);
`;

export const StyledOrderTypeSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
`;

export const StyledOrderTypeSelectItem = styled.div`
  display: flex;
  align-items: start;
  justify-content: start;
  gap: 10px;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  border: 1px solid var(--color-border-light);
  padding: 16px 20px;
  cursor: pointer;
  transition: border-color 0.3s ease, color 0.3s ease, opacity 0.3s ease;

  &:hover {
    border-color: #155eef;
    color: #155eef;
  }
`;

export const OrderInformationSection = styled.div`
  background-color: #f9fafb;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-black-bolder);
    margin: 0 0 16px 0;
  }
`;

export const OrderInfoRow = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const OrderInfoField = styled.div`
  .ant-form-item {
    margin-bottom: 0;

    .ant-form-item-label {
      text-align: left;

      > label {
        text-align: left;
        justify-content: flex-start;
      }
    }
  }
`;

export const ShippingInformationSection = styled.div`
  background-color: #f9fafb;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 20px;

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-black-bolder);
    margin: 0 0 16px 0;
  }
`;

export const ShippingInfoRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const ShippingInfoField = styled.div`
  label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 4px;
  }

  .value {
    font-size: 14px;
    color: #111827;
    display: flex;
    align-items: center;

    a {
      color: #2563eb;
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

export const DeleteIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 34px;
`;

export const DeleteIcon = styled(DeleteFilled)`
  color: #f04438;
  cursor: pointer;
  font-size: 20px;
`;

export const AddItemButton = styled(Button)`
  width: 100%;
  height: 50px;
  font-size: 15px;
`;

export const OrderItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  background-color: #f9fafb;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 20px;

  .ant-form-item {
    .ant-form-item-label {
      text-align: left;

      > label {
        text-align: left;
        justify-content: flex-start;
      }
    }
  }
`;

export const OrderProductsDivider = styled(Divider)`
  margin: 8px 0;
  border-color: var(--color-border-light);
`;

export const PickDesignButton = styled(Button)`
  width: 100%;
  height: 50px;
  border-radius: 50px;
`;

export const PhoneCaseEditorContainer = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  margin-top: 20px;
  border: 1px solid var(--color-border);
  border-radius: 8px;
  background-color: #ffffff;

  /* Ensure the editor content doesn't overflow */
  > * {
    max-width: 100%;
    box-sizing: border-box;
  }

  /* Handle any canvas or large elements */
  canvas,
  img,
  .phone-editor-content {
    max-width: 100% !important;
    height: auto !important;
  }

  @media (max-width: 1024px) {
    margin-left: -20px;
    margin-right: -20px;
    border-radius: 0;
    border-left: none;
    border-right: none;
  }
`;
